import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, X, Sparkles } from "lucide-react";

const AnimatedBlock = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const workItems = [
  "sciogliere i blocchi che ancora limitano la tua espansione",
  "integrare le tue Ombre senza combatterle",
  "rafforzare il tuo sistema nervoso affinch\u00e9 possa sostenere una vita pi\u00f9 grande",
  "sviluppare fiducia profonda nella tua intuizione",
  "incarnare una nuova identit\u00e0",
  "portare la tua missione nella vita quotidiana",
  "vivere con maggiore autenticit\u00e0, presenza e libert\u00e0",
];

const forYou = [
  "senti che \u00e8 arrivato il momento di fare un salto di identit\u00e0",
  "desideri una guida che ti accompagni personalmente",
  "sei pronta/o ad assumerti pienamente la responsabilit\u00e0 della tua evoluzione",
  "non vuoi pi\u00f9 rimandare la vita che senti di essere venuta/o a vivere",
  "desideri trasformare la tua spiritualit\u00e0 in qualcosa di concreto e incarnato",
  "vuoi essere vista/o, accompagnata/o e sostenuta/o durante il cambiamento",
];

const notForYou = [
  "cerchi una soluzione veloce.",
  "desideri che qualcun altro faccia il lavoro al posto tuo.",
  "non sei pronta/o ad attraversare ci\u00f2 che emerger\u00e0.",
  "preferisci restare nella teoria piuttosto che vivere una trasformazione reale.",
];

const OltreSection = () => {
  return (
    <section data-testid="oltre-section" className="py-24 md:py-32 bg-[#ECEAE6]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">

        {/* Title */}
        <AnimatedBlock>
          <div className="text-center mb-4">
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#442F23] mb-3">
              OLTRE
            </h2>
            <p className="font-heading text-lg md:text-xl text-[#AC8E60] font-medium max-w-2xl mx-auto">
              Il Mentoring Privato per chi &egrave; pronta/o a incarnare il proprio prossimo livello
            </p>
          </div>
        </AnimatedBlock>

        {/* Locandina */}
        <AnimatedBlock delay={0.05}>
          <div className="mb-12 max-w-3xl mx-auto">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(68,47,35,0.1)]">
              <img
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_inner-light-path-2/artifacts/ubyx3kkn_OLTRE%20LWH%201.png"
                alt="OLTRE - Light Workers House + Mentoring 1:1"
                className="w-full h-auto object-cover"
                data-testid="oltre-locandina"
              />
            </div>
          </div>
        </AnimatedBlock>

        {/* Opening */}
        <AnimatedBlock delay={0.1}>
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Ci sono momenti nel cammino dell'anima in cui la crescita personale non basta pi&ugrave;.
            </p>
            <p className="font-body text-base text-[#442F23]/70 leading-relaxed">
              Hai gi&agrave; letto libri. Hai gi&agrave; fatto corsi. Hai gi&agrave; attraversato guarigioni profonde. Hai gi&agrave; compreso tante cose di te.
            </p>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Eppure... <strong>senti che qualcosa continua a trattenerti.</strong>
            </p>
            <p className="font-body text-base text-[#442F23]/70 leading-relaxed">
              Come se una parte di te sapesse esattamente chi potrebbe diventare... mentre un'altra continuasse a tenerla nascosta.
            </p>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed font-medium">
              Non hai bisogno di altre informazioni.<br />
              <strong>Hai bisogno di incarnare.</strong><br />
              Hai bisogno di attraversare quella soglia che da sola continui a rimandare.
            </p>
          </div>
        </AnimatedBlock>

        {/* OLTRE nasce per questo */}
        <AnimatedBlock delay={0.15}>
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <div className="section-divider mb-8" />
            <p className="font-heading text-2xl md:text-3xl font-semibold text-[#442F23]">
              OLTRE nasce per questo.
            </p>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Per accompagnare anime che sentono di essere pronte per il loro prossimo livello.
            </p>
            <p className="font-body text-base text-[#442F23]/70 leading-relaxed">
              Non &egrave; un altro percorso. <strong>&Egrave; un'iniziazione.</strong>
            </p>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Un viaggio di sei mesi in cui lavoreremo insieme affinch&eacute; tu possa lasciare andare la versione di te che sopravvive... e iniziare finalmente a vivere come la persona che la tua anima &egrave; venuta ad essere.
            </p>
          </div>
        </AnimatedBlock>

        {/* Forse ti riconosci qui */}
        <AnimatedBlock delay={0.2}>
          <div className="bg-[#FBF8E9] border border-[#AC8E60]/15 rounded-[2rem] p-8 lg:p-10 mb-16">
            <h3 className="font-heading text-2xl font-semibold text-[#442F23] mb-6 text-center">
              Forse ti riconosci qui...
            </h3>
            <div className="max-w-2xl mx-auto space-y-4">
              <p className="font-body text-sm text-[#442F23]/80 leading-relaxed">Sai di avere un potenziale molto pi&ugrave; grande di quello che stai vivendo.</p>
              <p className="font-body text-sm text-[#442F23]/80 leading-relaxed">Senti di avere doni da offrire al mondo, ma qualcosa continua a frenarti.</p>
              <p className="font-body text-sm text-[#442F23]/80 leading-relaxed">Hai fatto tanto lavoro su di te, ma continui a ritrovarti negli stessi schemi.</p>
              <p className="font-body text-sm text-[#442F23]/80 leading-relaxed">Ti senti pronta/o a espanderti... ma una parte di te ha ancora paura di essere vista/o.</p>
              <p className="font-body text-sm text-[#442F23]/80 leading-relaxed">Desideri vivere con pi&ugrave; libert&agrave;, autenticit&agrave; e potere... ma senti che ti manca qualcuno che sappia vedere ci&ograve; che tu ancora non riesci a vedere.</p>
              <p className="font-body text-base text-[#AC8E60] text-center mt-8 font-medium italic">
                Se leggendo queste parole hai sentito un &ldquo;s&igrave;&rdquo; dentro...<br />
                OLTRE &egrave; stato creato per te.
              </p>
            </div>
          </div>
        </AnimatedBlock>

        {/* Cosa rende OLTRE diverso */}
        <AnimatedBlock delay={0.25}>
          <div className="max-w-3xl mx-auto mb-16">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#442F23] mb-6 text-center">
              Cosa rende OLTRE diverso?
            </h3>
            <div className="space-y-4 text-center">
              <p className="font-body text-sm text-[#442F23]/70 leading-relaxed">
                Light Workers House ti offre la forza del gruppo. La bellezza della Community. L'energia del Cerchio. La pratica costante.
              </p>
              <p className="font-body text-base text-[#442F23]/80 leading-relaxed font-medium">
                OLTRE aggiunge qualcosa di diverso.<br />
                <strong>La mia presenza al tuo fianco.</strong>
              </p>
              <p className="font-body text-sm text-[#442F23]/70 leading-relaxed">
                Uno spazio completamente dedicato a te. Alla tua storia. Alle tue ferite. Ai tuoi doni. Alla missione che la tua anima desidera incarnare.
              </p>
              <p className="font-body text-sm text-[#442F23]/70 leading-relaxed">
                Per sei mesi cammineremo insieme. Non seguirai un programma uguale per tutti. Ogni incontro nascer&agrave; da ci&ograve; che emerger&agrave; dentro di te. Perch&eacute; nessuna anima compie il proprio viaggio nello stesso modo.
              </p>
            </div>
          </div>
        </AnimatedBlock>

        {/* In questi sei mesi lavoreremo per... */}
        <AnimatedBlock delay={0.3}>
          <div className="max-w-2xl mx-auto mb-16">
            <h3 className="font-heading text-xl font-semibold text-[#442F23] mb-6 text-center">
              In questi sei mesi lavoreremo insieme per...
            </h3>
            <ul className="space-y-3">
              {workItems.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Sparkles size={16} className="text-[#E1C340] flex-shrink-0 mt-1" />
                  <span className="font-body text-sm text-[#442F23]/80">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 text-center">
              <p className="font-body text-sm text-[#442F23]/70 leading-relaxed">
                Non lavoreremo solo sulla consapevolezza. <strong>Lavoreremo sull'incarnazione.</strong>
              </p>
              <p className="font-body text-sm text-[#AC8E60] mt-2 italic">
                Perch&eacute; comprendere cambia la mente. Incarnare cambia la vita.
              </p>
            </div>
          </div>
        </AnimatedBlock>

        {/* Il tuo percorso comprende */}
        <AnimatedBlock delay={0.35}>
          <div className="bg-[#FBF8E9] border border-[#E1C340]/20 rounded-[2rem] p-8 lg:p-10 mb-16">
            <h3 className="font-heading text-2xl font-semibold text-[#442F23] mb-8 text-center">
              Il tuo percorso comprende
            </h3>

            <div className="space-y-8">
              <div>
                <h4 className="font-heading text-lg font-semibold text-[#442F23] mb-2">Il percorso completo Light Workers House</h4>
                <p className="font-body text-sm text-[#442F23]/70">con tutti i contenuti del percorso di gruppo.</p>
              </div>

              <div>
                <h4 className="font-heading text-lg font-semibold text-[#442F23] mb-2">Mentoring Privato con Alessia</h4>
                <p className="font-body text-sm text-[#442F23]/70 mb-1">Un percorso individuale costruito completamente intorno a te.</p>
                <p className="font-body text-sm text-[#AC8E60] font-semibold mb-3">10 Sessioni trasformative da 90 minuti</p>
                <p className="font-body text-sm text-[#442F23]/70 mb-3">Ogni sessione nasce da ci&ograve; che il tuo percorso richiede in quel momento.</p>
                <p className="font-body text-sm text-[#442F23]/60 mb-2">Potremo utilizzare, a seconda delle necessit&agrave;:</p>
                <ul className="space-y-1">
                  {["Soul Coaching", "Shadow Work", "lavoro sul sistema nervoso", "pratiche somatiche", "embodiment", "meditazioni", "visualizzazioni", "lavoro energetico", "tecniche di riprogrammazione subconscia"].map((t) => (
                    <li key={t} className="flex items-center gap-2 font-body text-sm text-[#442F23]/65">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#E1C340] flex-shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
                <p className="font-body text-sm text-[#442F23]/60 mt-2 italic">e tutti gli strumenti che riterr&ograve; pi&ugrave; efficaci per accompagnare la tua trasformazione.</p>
              </div>

              <div>
                <h4 className="font-heading text-lg font-semibold text-[#442F23] mb-2">Supporto personale tra una sessione e l'altra</h4>
                <p className="font-body text-sm text-[#442F23]/70">Perch&eacute; la trasformazione non avviene solo durante gli incontri. Avrai uno spazio diretto con me per condividere dubbi, integrazioni, intuizioni e ricevere sostegno durante tutto il percorso.</p>
              </div>
            </div>
          </div>
        </AnimatedBlock>

        {/* Per chi è / Non è */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <AnimatedBlock delay={0.4}>
            <div className="bg-[#FBF8E9] border border-[#AC8E60]/15 rounded-[2rem] p-8">
              <h3 className="font-heading text-xl font-semibold text-[#442F23] mb-6">
                OLTRE &egrave; per te se...
              </h3>
              <ul className="space-y-3">
                {forYou.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check size={16} className="text-[#E1C340] flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#442F23]/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedBlock>

          <AnimatedBlock delay={0.45}>
            <div className="bg-[#FBF8E9] border border-[#AC8E60]/10 rounded-[2rem] p-8">
              <h3 className="font-heading text-xl font-semibold text-[#442F23] mb-6">
                OLTRE non &egrave; per te se...
              </h3>
              <ul className="space-y-3">
                {notForYou.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X size={16} className="text-[#AC8E60]/50 flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#442F23]/65">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedBlock>
        </div>

        {/* Immagina tra 6 mesi */}
        <AnimatedBlock delay={0.5}>
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#442F23] mb-4">
              Immagina di essere tra sei mesi...
            </h3>
            <p className="font-body text-sm text-[#442F23]/75 leading-relaxed">
              Ti svegli al mattino e senti una pace nuova. Non perch&eacute; la vita sia perfetta. Ma perch&eacute; finalmente sei allineata/o.
            </p>
            <p className="font-body text-sm text-[#442F23]/75 leading-relaxed">
              Non dubiti pi&ugrave; del tuo valore. Non ti nascondi pi&ugrave;. Non vivi pi&ugrave; per adattarti.
            </p>
            <p className="font-body text-sm text-[#442F23]/75 leading-relaxed">
              Ti fidi della tua intuizione. Sai sostenere le tue emozioni. Prendi decisioni con chiarezza. La tua energia &egrave; stabile. Il tuo corpo &egrave; un luogo sicuro. La tua anima guida ogni scelta.
            </p>
            <p className="font-body text-base text-[#AC8E60] font-medium mt-4 italic">
              E la missione che un tempo sembrava cos&igrave; lontana...<br />
              ora &egrave; diventata il tuo modo di vivere.
            </p>
          </div>
        </AnimatedBlock>

        {/* Messaggio da me */}
        <AnimatedBlock delay={0.55}>
          <div className="bg-[#442F23] rounded-[2rem] p-8 lg:p-10 mb-16">
            <h3 className="font-heading text-xl font-semibold text-[#E1C340] mb-6 text-center">
              Un messaggio da me
            </h3>
            <div className="max-w-2xl mx-auto space-y-4 text-center">
              <p className="font-body text-sm text-[#FBF8E9]/80 leading-relaxed">
                Ho creato OLTRE pensando a quella versione di me che aveva gi&agrave; fatto tantissimo lavoro su di s&eacute;. Avevo studiato. Avevo compreso. Avevo guarito molte ferite.
              </p>
              <p className="font-body text-sm text-[#FBF8E9]/80 leading-relaxed">
                Eppure... continuavo a sentire che qualcosa mancava. Non mi servivano altre informazioni. Mi serviva qualcuno che vedesse la donna che stava cercando di emergere... prima ancora che riuscissi a vederla io.
              </p>
              <p className="font-body text-sm text-[#E1C340] font-medium">
                OLTRE &egrave; il percorso che avrei desiderato incontrare in quel momento. Ed &egrave; il modo in cui oggi accompagno chi sente di essere pronta/o a fare quel passo.
              </p>
            </div>
          </div>
        </AnimatedBlock>

        {/* Dettaglio importante */}
        <AnimatedBlock delay={0.6}>
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="font-heading text-xl font-semibold text-[#442F23] mb-4">
              Un dettaglio importante
            </h3>
            <p className="font-body text-sm text-[#442F23]/75 leading-relaxed mb-4">
              Il mentoring individuale inizier&agrave; a settembre. Ma il tuo viaggio inizia nel momento in cui entri.
            </p>
            <p className="font-body text-sm text-[#442F23]/75 leading-relaxed mb-4">
              Appena ti iscriverai riceverai immediatamente l'accesso a Light Workers House. Potrai iniziare subito a partecipare alla Community, alle Live, ai Cerchi di Luce e a tutta la libreria delle pratiche.
            </p>
            <p className="font-body text-sm text-[#AC8E60] font-medium italic">
              Quando inizieremo il lavoro individuale... non partiremo da zero. Arriverai con fondamenta solide. E questo render&agrave; il mentoring ancora pi&ugrave; potente.
            </p>
          </div>
        </AnimatedBlock>
      </div>
    </section>
  );
};

export default OltreSection;
